import * as React from 'react';
import './NotificationsMenu.scss';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import PaymentIcon from '@material-ui/icons/Payment';
import SmsIcon from '@material-ui/icons/Sms';
import CakeIcon from '@material-ui/icons/Cake';
import AssignmentLateIcon from '@material-ui/icons/AssignmentLate';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import UpdateIcon from '@material-ui/icons/Update';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { DropdownItem, Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { IconButton, Badge } from '@material-ui/core';
import { Notification, NotificationTypeEnum } from '../../../models';

export interface NotificationsMenuStateProps {
    notifications: Notification[],
}

export interface NotificationsMenuDispatchProps {
    onNotificationClick: (notification: Notification) => void,
}

export type NotificationsMenuProps = NotificationsMenuStateProps & NotificationsMenuDispatchProps;

type NotificationVisualTone = 'warning' | 'danger' | 'info' | 'success' | 'neutral';

const getNotificationVisuals = (type: NotificationTypeEnum): { icon: React.ReactNode; label: string; tone: NotificationVisualTone } => {
    switch (type) {
        case NotificationTypeEnum.AppointmentMissingPayment:
            return { icon: <PaymentIcon fontSize="small" />, label: 'Pagamento', tone: 'danger' };
        case NotificationTypeEnum.SmsReceived:
            return { icon: <SmsIcon fontSize="small" />, label: 'SMS', tone: 'info' };
        case NotificationTypeEnum.ClientBirthday:
            return { icon: <CakeIcon fontSize="small" />, label: 'Aniversário', tone: 'success' };
        case NotificationTypeEnum.PackMissingPayment:
            return { icon: <AssignmentLateIcon fontSize="small" />, label: 'Pack', tone: 'warning' };
        case NotificationTypeEnum.ClientRequest:
            return { icon: <MailOutlineIcon fontSize="small" />, label: 'Pedido', tone: 'info' };
        case NotificationTypeEnum.ClientInactive:
            return { icon: <PersonOutlineIcon fontSize="small" />, label: 'Inativo', tone: 'neutral' };
        case NotificationTypeEnum.ClientFollowUp:
            return { icon: <UpdateIcon fontSize="small" />, label: 'Follow-up', tone: 'info' };
        case NotificationTypeEnum.OrderMissingPayment:
            return { icon: <ShoppingCartIcon fontSize="small" />, label: 'Encomenda', tone: 'danger' };
        default:
            return { icon: <NotificationImportantIcon fontSize="small" />, label: 'Alerta', tone: 'neutral' };
    }
};

const getNotificationKey = (notification: Notification): string =>
    `${notification.type}-${notification.date}-${notification.description}-${notification.entityId || 'no-entity'}-${notification.value || 'no-value'}`;

const NotificationsMenu: React.FC<NotificationsMenuProps> = (props) => {
    const {
        notifications = [],
        onNotificationClick,
    } = props;

    const [isOpen, setOpen] = React.useState(false);

    return !notifications.length ? null : 
        <Dropdown className="notificationsMenuContainer" isOpen={isOpen} toggle={() => setOpen(!isOpen)}>
            <DropdownToggle nav className="notificationsButton">
                <IconButton>
                    <Badge badgeContent={notifications.length} color="secondary">
                        <NotificationImportantIcon/>
                    </Badge>
                </IconButton>
            </DropdownToggle>

            <DropdownMenu className="notificationsList" right>
                { notifications.map((notification) => {
                    const { icon, label, tone } = getNotificationVisuals(notification.type);

                    return (
                    <DropdownItem 
                        key={getNotificationKey(notification)}
                        className="notificationItem"
                        onClick={() => onNotificationClick(notification)}
                    >
                        <span className={`notificationIcon notificationIcon-${tone}`}>{icon}</span>
                        <span className="notificationBody">
                            <span className="notificationMetaRow">
                                <span className={`notificationTypeBadge notificationTypeBadge-${tone}`}>{label}</span>
                                <span className="notificationDate">{notification.date}</span>
                            </span>
                            <span className="notificationDescription">{notification.description}</span>
                        </span>
                        { notification.value && <span className="notificationValue">{notification.value}</span> }
                    </DropdownItem>
                )})}
            </DropdownMenu> 
        </Dropdown>
}

export default NotificationsMenu;
